/**
 * NetobsBroadcastPublisher (parent-side) — every ~250ms, snapshots
 * the parent window's netobs state and publishes it to the
 * BroadcastChannel for any popout windows to consume.
 *
 * Mounts in main.tsx alongside the other netobs bridges. Only runs
 * when isNetobsEnabled() AND we're NOT in popout mode (popout doesn't
 * publish to itself). The publish is a single postMessage call per
 * tick — cheap, no measurable overhead even with no listener.
 *
 * What's published:
 *   - 'tick'    — latest TickHealthSnapshot from server (if seen)
 *   - 'traffic' — current NetworkTracer ring + per-type stats
 *   - 'latency' — latest aggregate broadcast
 *
 * Frame size guidance: traffic ring is 300 events × small JSON each;
 * the typical postMessage payload runs ~30-60KB which BroadcastChannel
 * handles fine on modern browsers.
 */

import React, { useEffect, useRef } from 'react';
import { useColyseusMessages } from '@/game/ui/hooks/useColyseusMessages';
import { isNetobsEnabled } from '../isNetobsEnabled';
import { isPopoutMode } from './popoutMode';
import {
  publishNetobsFrame,
  type MessageTypeStatsRecord,
  type NetobsLatencyFrame,
  type NetobsTickFrame,
  type NetobsTrafficFrame,
} from './popoutChannel';

const PUBLISH_INTERVAL_MS = 250;

const NetobsBroadcastPublisherInner: React.FC = () => {
  const lastTickRef = useRef<NetobsTickFrame['payload'] | null>(null);
  const lastLatencyRef = useRef<NetobsLatencyFrame['payload'] | null>(null);

  // Capture netobs:tick + netobs:latency_aggregate as they arrive.
  // We re-publish on the 250ms cadence rather than on receive, so a
  // popout opened mid-session immediately gets the most recent value
  // even if no fresh broadcast has happened yet.
  useColyseusMessages({
    'netobs:tick': (msg: any) => {
      if (!msg) return;
      lastTickRef.current = {
        meanMs: Number(msg.meanMs) || 0,
        maxMs: Number(msg.maxMs) || 0,
        sampleCount: Number(msg.sampleCount) || 0,
        budgetMs: Number(msg.budgetMs) || 16.67,
        timestamp: Number(msg.timestamp) || Date.now(),
      };
    },
    'netobs:latency_aggregate': (msg: any) => {
      if (!msg || !Array.isArray(msg.snaps)) return;
      lastLatencyRef.current = {
        snaps: msg.snaps,
        timestamp: Number(msg.timestamp) || Date.now(),
      };
    },
  });

  useEffect(() => {
    const tick = () => {
      // Tick frame
      if (lastTickRef.current) {
        publishNetobsFrame({ type: 'tick', payload: lastTickRef.current });
      }
      // Latency frame
      if (lastLatencyRef.current) {
        publishNetobsFrame({ type: 'latency', payload: lastLatencyRef.current });
      }
      // Traffic frame — read live from NetworkTracer
      const tracer = (globalThis as any).nm?.tracer;
      if (tracer && Array.isArray(tracer.recentTraffic)) {
        const recentTraffic = tracer.recentTraffic.map((ev: any) => ({
          seq: ev.seq,
          timestamp: ev.timestamp,
          direction: ev.direction,
          type: ev.type,
          bytes: ev.bytes,
        }));
        const outboundStats: Array<[string, MessageTypeStatsRecord]> = [];
        const inboundStats: Array<[string, MessageTypeStatsRecord]> = [];
        if (tracer.outboundMessageTypeStats instanceof Map) {
          for (const [k, v] of tracer.outboundMessageTypeStats) {
            outboundStats.push([k, { ...v }]);
          }
        }
        if (tracer.inboundMessageTypeStats instanceof Map) {
          for (const [k, v] of tracer.inboundMessageTypeStats) {
            inboundStats.push([k, { ...v }]);
          }
        }
        const frame: NetobsTrafficFrame = {
          type: 'traffic',
          payload: { recentTraffic, outboundStats, inboundStats },
        };
        publishNetobsFrame(frame);
      }
    };
    const id = setInterval(tick, PUBLISH_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return null;
};

export const NetobsBroadcastPublisher: React.FC = () => {
  // Only the parent (game) window publishes. Popout window doesn't
  // publish back to itself.
  if (!isNetobsEnabled()) return null;
  if (isPopoutMode()) return null;
  return <NetobsBroadcastPublisherInner />;
};
